/**
 * useSidebarResize - shared sidebar resize + oversteer logic.
 *
 * Oversteer: When the sidebar handle is dragged past its bounds,
 * the main navigation is steered:
 * - LEFT:  Sidebar collapsed + keep dragging left  → main nav shrinks
 * - RIGHT: Sidebar at MAX    + keep dragging right → main nav grows
 */

import { useCallback, useRef } from 'react';
import { mainNavStore, NAV_STAGE_WIDTHS } from '../stores/mainNavStore';
import { setDragBodyStyles, stopDragBodyStyles } from './dragResizeUtils';

export interface SidebarResizeConfig {
  storageKeyWidth: string;
  storageKeyCollapsed: string;
  min: number;
  max: number;
  defaultWidth: number;
  collapsedWidth: number;
  collapseThreshold: number;
  oversteer: boolean;
  side?: 'left' | 'right';
  onSidebarUpdate: (patch: {
    sidebarWidth?: number;
    isCollapsed?: boolean;
    isSidebarResizing?: boolean;
  }) => void;
}

type OversteerDir = 'none' | 'left' | 'right';

export function loadSidebarInitialWidth(config: Pick<SidebarResizeConfig, 'storageKeyWidth' | 'min' | 'max' | 'defaultWidth'>): number {
  try {
    const stored = localStorage.getItem(config.storageKeyWidth);
    if (stored) {
      const val = Number(stored);
      if (val >= config.min && val <= config.max) return val;
    }
  } catch {}
  return config.defaultWidth;
}

export function loadSidebarInitialCollapsed(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {}
  return false;
}

export function useSidebarResize(config: SidebarResizeConfig) {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const expandedWidthRef = useRef(loadSidebarInitialWidth(config));
  const collapsedRef = useRef(loadSidebarInitialCollapsed(config.storageKeyCollapsed));

  // Keep callback ref fresh to avoid stale closures
  const onUpdateRef = useRef(config.onSidebarUpdate);
  onUpdateRef.current = config.onSidebarUpdate;

  // Oversteer refs
  const oversteerRef = useRef<OversteerDir>('none');
  const oversteerStartNavWidthRef = useRef(0);
  const oversteerNavClampRef = useRef(0);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = collapsedRef.current ? config.collapsedWidth : expandedWidthRef.current;
    onUpdateRef.current({ isSidebarResizing: true });

    setDragBodyStyles('col-resize');

    function endOversteer() {
      if (oversteerRef.current !== 'none') {
        oversteerRef.current = 'none';
        const navCtx = mainNavStore.getSnapshot().context;
        if (navCtx.isDragging) {
          mainNavStore.send({ type: 'nav.endDrag' });
        }
      }
    }

    function beginOversteer(dir: OversteerDir) {
      if (oversteerRef.current !== 'none' && oversteerRef.current !== dir) {
        endOversteer();
      }
      if (oversteerRef.current === dir) return;

      const navCtx = mainNavStore.getSnapshot().context;
      const navW = navCtx.rawWidth;
      oversteerStartNavWidthRef.current = navW;
      oversteerNavClampRef.current = navW;
      if (!navCtx.isDragging) {
        mainNavStore.send({ type: 'nav.startDrag' });
      }
      oversteerRef.current = dir;
    }

    const handleMouseMove = (ev: MouseEvent) => {
      const rawDelta = ev.clientX - startXRef.current;
      const delta = config.side === 'right' ? -rawDelta : rawDelta;
      const rawWidth = startWidthRef.current + delta;

      // LEFT OVERSTEER
      if (rawWidth < config.collapseThreshold) {
        collapsedRef.current = true;

        if (config.oversteer) {
          beginOversteer('left');
          const oversteerDelta = config.collapseThreshold - rawWidth;
          const rawNavWidth = oversteerStartNavWidthRef.current - oversteerDelta;
          oversteerNavClampRef.current = Math.min(oversteerNavClampRef.current, rawNavWidth);
          const newNavWidth = Math.max(NAV_STAGE_WIDTHS['tiny'], oversteerNavClampRef.current);
          mainNavStore.send({ type: 'nav.setRawWidth', width: newNavWidth });
        }

        onUpdateRef.current({ sidebarWidth: config.collapsedWidth, isCollapsed: true });
        return;
      }

      // RIGHT OVERSTEER
      if (rawWidth > config.max) {
        collapsedRef.current = false;
        expandedWidthRef.current = config.max;

        if (config.oversteer) {
          beginOversteer('right');
          const oversteerDelta = rawWidth - config.max;
          const rawNavWidth = oversteerStartNavWidthRef.current + oversteerDelta;
          oversteerNavClampRef.current = Math.max(oversteerNavClampRef.current, rawNavWidth);
          const newNavWidth = Math.min(NAV_STAGE_WIDTHS['expanded'], oversteerNavClampRef.current);
          mainNavStore.send({ type: 'nav.setRawWidth', width: newNavWidth });
        }

        onUpdateRef.current({ sidebarWidth: config.max, isCollapsed: false });
        return;
      }

      // NORMAL
      if (config.oversteer) endOversteer();
      const newWidth = Math.max(config.min, Math.min(config.max, rawWidth));
      collapsedRef.current = false;
      expandedWidthRef.current = newWidth;
      onUpdateRef.current({ sidebarWidth: newWidth, isCollapsed: false });
    };

    const handleMouseUp = () => {
      if (config.oversteer) endOversteer();
      onUpdateRef.current({ isSidebarResizing: false });
      stopDragBodyStyles();

      try {
        localStorage.setItem(config.storageKeyWidth, String(expandedWidthRef.current));
        localStorage.setItem(config.storageKeyCollapsed, String(collapsedRef.current));
      } catch {}

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [config.collapsedWidth, config.collapseThreshold, config.max, config.min, config.oversteer, config.side, config.storageKeyCollapsed, config.storageKeyWidth]);

  const handleDoubleClick = useCallback(() => {
    const newCollapsed = !collapsedRef.current;
    collapsedRef.current = newCollapsed;
    const width = newCollapsed ? config.collapsedWidth : expandedWidthRef.current;
    onUpdateRef.current({ sidebarWidth: width, isCollapsed: newCollapsed });
    try {
      localStorage.setItem(config.storageKeyCollapsed, String(newCollapsed));
    } catch {}
  }, [config.collapsedWidth, config.storageKeyCollapsed]);

  const toggleCollapse = useCallback(() => {
    const newCollapsed = !collapsedRef.current;
    collapsedRef.current = newCollapsed;
    const width = newCollapsed ? config.collapsedWidth : expandedWidthRef.current;
    onUpdateRef.current({ sidebarWidth: width, isCollapsed: newCollapsed });
    try {
      localStorage.setItem(config.storageKeyCollapsed, String(newCollapsed));
    } catch {}
  }, [config.collapsedWidth, config.storageKeyCollapsed]);

  return { handleResizeStart, handleDoubleClick, toggleCollapse };
}
